import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { TrendingUp, ChevronLeft, CheckCircle, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import { toast } from 'sonner';

export default function AdminClaims() {
  const { api } = useAuth();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchClaims();
  }, [page, statusFilter]);

  const fetchClaims = async () => {
    try {
      let url = `/admin/claims?page=${page}&limit=20`;
      if (statusFilter && statusFilter !== 'all') url += `&status=${statusFilter}`;
      const response = await api.get(url);
      setClaims(response.data.claims);
      setTotalPages(response.data.total_pages);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (claimId, status) => {
    try {
      await api.put(`/admin/claims/${claimId}/review?status=${status}`);
      toast.success(`Solicitação ${status === 'approved' ? 'aprovada' : 'rejeitada'}`);
      fetchClaims();
    } catch (error) {
      toast.error('Erro ao processar solicitação');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pendente', className: 'bg-amber-100 text-amber-700' },
      approved: { label: 'Aprovada', className: 'bg-emerald-100 text-emerald-700' },
      rejected: { label: 'Rejeitada', className: 'bg-red-100 text-red-700' },
    };
    const { label, className } = statusMap[status] || statusMap.pending;
    return <Badge className={className}>{label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-claims">
      <Navbar />

      <div className="container-app py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Reivindicações</h1>
            <p className="text-slate-600 mt-1">Aprovar reivindicações de perfis</p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Todos os status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="pending">Pendentes</SelectItem>
              <SelectItem value="approved">Aprovadas</SelectItem>
              <SelectItem value="rejected">Rejeitadas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-4">
          {loading ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse h-24 bg-white rounded-2xl"></div>
            ))
          ) : claims.length > 0 ? (
            claims.map((claim) => (
              <div key={claim.id} className="bg-white rounded-2xl border border-slate-100 p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusBadge(claim.status)}
                      <Badge variant="secondary">{claim.profile_type}</Badge>
                    </div>
                    <p className="text-sm text-slate-600">ID do Perfil: {claim.profile_id}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      Criado em {format(new Date(claim.created_at), 'dd/MM/yyyy HH:mm')}
                    </p>
                  </div>
                  {claim.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-emerald-600"
                        onClick={() => handleReview(claim.id, 'approved')}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Aprovar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600"
                        onClick={() => handleReview(claim.id, 'rejected')}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Rejeitar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl">
              <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nenhuma reivindicação encontrada</p>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              Anterior
            </Button>
            <span className="flex items-center px-4 text-sm text-slate-600">
              Página {page} de {totalPages}
            </span>
            <Button variant="outline" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
              Próxima
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
